/**
 * 멘션 분석 3화면의 **기능 스위치**.
 *
 * 켜고 끄면 조회 동작이 바뀌는 값만 여기 둔다. 판정 *로직*은 각 유틸에 있고
 * (`utils/exclude-keyword-query.ts` · `utils/moments/moment-data-source-mode.ts` ·
 * `utils/brand/brand-filter-utils.ts`), 이 파일은 그 로직이 읽는 **설정**이다.
 *
 * 조정 가능한 스위치는 로직 파일에 흩어 놓지 말고 전부 여기에 모은다.
 *
 * ⚠️ 이 파일은 `src/utils`를 import 하지 않는다 — 설정이 로직에 의존하면 순환이 된다.
 */

// ─────────────────────────────────────────────────────────────
// 데이터 소스 모드 (CORPUS ↔ API)
// ─────────────────────────────────────────────────────────────

/**
 * | 모드 | 데이터 흐름 |
 * |---|---|
 * | CORPUS | 조회기간 전건을 메모리에 올리고 클라이언트에서 필터·정렬·페이지·집계 |
 * | API | 탭별 ES 조회 (대시보드 집계 / 게시글 페이지 / CA 전건) |
 */
export const MOMENT_DATA_SOURCE_MODES = Object.freeze({
  CORPUS: 'corpus',
  API: 'api',
});

/** 오버라이드·특문 분기에 걸리지 않을 때의 기본 모드. */
export const DEFAULT_MOMENT_DATA_SOURCE_MODE = MOMENT_DATA_SOURCE_MODES.API;

/**
 * 이 개수 이상의 제외 키워드가 저장돼 있으면 CORPUS로 보낸다.
 * 개수가 늘수록 API 모드의 `must_not` 조합 비용이 커져 코퍼스 클라 매칭이 유리해진다.
 */
export const EXCLUDE_KEYWORD_CORPUS_THRESHOLD = 4;

// ─────────────────────────────────────────────────────────────
// 제외 키워드 ES 반영
// ─────────────────────────────────────────────────────────────

/**
 * 제외 키워드를 ES 쿼리(`must_not`)에 반영할지 — 화면별.
 * false면 제외 must_not 절이 아예 빠져 이전 쿼리와 동일해진다 (A/B 비교·롤백용).
 * 검색 화면에는 제외 키워드 기능이 없다.
 */
export const BRAND_EXCLUDE_KEYWORDS_ES_ENABLED = true;

/** 키워드 모니터링 — 위와 동일. */
export const KEYWORD_EXCLUDE_KEYWORDS_ES_ENABLED = true;

/**
 * 특수문자 제외어(".vt" 등)를 runtime blob + painless contains로 ES에서 제외할지 — 화면별.
 * false면 특수문자 제외어는 ES 절에서 스킵된다(일반 제외어는 이 플래그와 무관하게 반영).
 *
 * ⚠️ blob 스크립트는 매칭 후보 문서마다 _source(searchPost 포함)를 파싱하고
 * ES 요청 캐시도 듣지 않는다 — 특수문자 제외어가 있는 워크스페이스는 조회당 20~44초를
 * **매번** 낸다(40건만 반환하는 게시글 1페이지도 44초).
 * 켜면 숫자가 정확해지고(코퍼스와 일치) 끄면 제외분만큼 과대 집계된다.
 *
 * 실제로는 데이터 소스 모드 분기가 특문 워크스페이스를 CORPUS로 보내 이 경로를
 * 사실상 우회한다. 근본 해법은 제외 매칭용 소문자 blob **색인 필드** 승격(백엔드 로드맵).
 */
export const BRAND_EXCLUDE_SPECIAL_KEYWORDS_VIA_BLOB = true;

/**
 * 키워드 모니터링 — 위와 동일. 브랜드와 값을 맞춰 켜 둔다.
 *
 * 켜도 실제 blob 비용을 내는 경우는 드물다 — 특문 제외어가 있는 캠페인은
 * `resolveKeywordDataSourceMode`가 CORPUS로 보내기 때문에 API 모드의 이 경로에
 * 도달하지 않는다. 즉 브랜드와 마찬가지로 **모드 분기로 사실상 사문화**된 상태이고,
 * 이 플래그는 모드 오버라이드(`?keywordDataSource=api`)로 API 모드를 강제했을 때의
 * 정확도를 결정한다 — 켜면 코퍼스와 숫자가 일치하고, 끄면 특문 제외분만큼 과대 집계된다.
 */
export const KEYWORD_EXCLUDE_SPECIAL_KEYWORDS_VIA_BLOB = true;

// ─────────────────────────────────────────────────────────────
// 브랜드 필터
// ─────────────────────────────────────────────────────────────

/**
 * 브랜드 X 서브타입(본문·인용·리포스트) 필터 스위치.
 *
 * 판정은 `isQuoted`·`isRepost` **색인 루트 플래그**라 doc_values `term`으로 싸게 평가된다.
 * ⚠️ `origin_post_type` painless runtime 스크립트로 판정하는 방식으로 가지 마라 — 문서마다
 * `_source`(searchPost 포함)를 파싱해, 이 필터가 걸린 워크스페이스의 **모든 쿼리**
 * (A·C·D·게시글·CA·Excel)에 쿼리당 +13.7초가 붙는다(14.9s ↔ 1.2s).
 *
 * false면 X 서브타입 선택을 "전체 X"로 취급해 필터 절을 생략한다(롤백용).
 */
export const BRAND_X_SUBTYPE_FILTER_ENABLED = true;
